import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Tab,
  ProgressBar,
  Nav,
  Spinner,
  Badge,
} from "react-bootstrap";
import API from "../services/api";
import "./SupplierProfileForm.css";
import { updateStoredUser } from "../utils/sessionUser";

const isValidGST = (v = "") =>
  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(String(v).trim());
const isValidPAN = (v = "") => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(String(v).trim());
const isValidMobile = (v = "") => /^[0-9]{10,15}$/.test(String(v || "").replace(/\D/g, ""));
const panFromGST = (gst = "") => String(gst || "").trim().toUpperCase().slice(2, 12);

const SupplierProfileForm = () => {
  const [form, setForm] = useState({
    logo: "",
    supplierName: "",
    companyName: "",
    gstNumber: "",
    panNumber: "",
    address: "",
    contactPerson: "",
    contactMobile: "",
    status: "active",
    bank: {
      accountName: "",
      accountNumber: "",
      ifsc: "",
      bankName: "",
      branch: "",
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [profileLocked, setProfileLocked] = useState(false);
  const [assignedManager, setAssignedManager] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [gstLookupLoading, setGstLookupLoading] = useState(false);
  const lastGstLookupRef = useRef("");
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [docMessage, setDocMessage] = useState("");
  const [docError, setDocError] = useState("");
  const [docs, setDocs] = useState({
    gstCertificate: null,
    panCard: null,
    bankProof: null,
    other: [],
  });
  const [docsExisting, setDocsExisting] = useState({
    gstCertificate: "",
    panCard: "",
    bankProof: "",
    other: [],
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");

  const fileRefs = {
    logo: useRef(null),
    gstCertificate: useRef(null),
    panCard: useRef(null),
    bankProof: useRef(null),
    other: useRef(null),
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/supplier/profile");
      if (res?.data) {
        const data = res.data;
        setProfileLocked(!!data.profileLocked);
        setAssignedManager(data.assignedManager || null);
        setForm({
          logo: data.logo || "",
          supplierName: data.supplierName || "",
          companyName: data.companyName || "",
          gstNumber: data.gstNumber || "",
          panNumber: data.panNumber || "",
          address: data.address || "",
          contactPerson: data.contactPerson || "",
          contactMobile: data.contactMobile || "",
          status: data.status || "active",
          bank: {
            accountName: data.bank?.accountName || "",
            accountNumber: data.bank?.accountNumber || "",
            ifsc: data.bank?.ifsc || "",
            bankName: data.bank?.bankName || "",
            branch: data.bank?.branch || "",
          },
        });
        setDocsExisting({
          gstCertificate: data.documents?.gstCertificate || "",
          panCard: data.documents?.panCard || "",
          bankProof: data.documents?.bankProof || "",
          other: Array.isArray(data.documents?.other) ? data.documents.other : [],
        });
        setLogoPreview(data.logo || "");
        updateStoredUser({
          fullName: data.companyName || data.supplierName || "Supplier",
          photo: data.logo || "",
          photoUpdatedAt: Date.now(),
        });
        setIsUpdate(true);
      }
    } catch (e) {
      setProfileLocked(false);
      setAssignedManager(null);
      if (e?.response?.status !== 404) {
        setError(e?.response?.data?.message || "Failed to load supplier profile");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const hasValue = (v) => String(v || "").trim().length > 0;

  const completion = useMemo(() => {
    const basicDone = [
      hasValue(form.companyName),
      hasValue(form.gstNumber) && isValidGST(form.gstNumber),
      hasValue(form.panNumber) && isValidPAN(form.panNumber),
      hasValue(form.address),
    ].filter(Boolean).length;

    const contactDone = [
      hasValue(form.contactPerson),
      hasValue(form.contactMobile),
    ].filter(Boolean).length;

    const bankDone = [
      hasValue(form.bank?.accountName),
      hasValue(form.bank?.accountNumber),
      hasValue(form.bank?.ifsc),
      hasValue(form.bank?.bankName),
    ].filter(Boolean).length;

    const docsDone = [
      hasValue(docsExisting.gstCertificate) || !!docs.gstCertificate,
      hasValue(docsExisting.panCard) || !!docs.panCard,
      hasValue(docsExisting.bankProof) || !!docs.bankProof,
      (docsExisting.other || []).length > 0 || (docs.other || []).length > 0,
    ].filter(Boolean).length;

    const sections = {
      basic: basicDone === 4,
      contact: contactDone >= 1,
      bank: bankDone >= 2,
      documents: docsDone >= 1,
    };

    const total = 4;
    const done = Object.values(sections).filter(Boolean).length;
    const percent = Math.round((done / total) * 100);

    return { percent, sections };
  }, [form, docs, docsExisting]);

  const setField = (name, value) => {
    setMessage("");
    setError("");
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("bank.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({ ...prev, bank: { ...prev.bank, [key]: value } }));
      return;
    }
    setField(name, value);
  };

  const applyGstProfileDetails = (data = {}) => {
    const companyName = data.companyName || data.tradeName || data.legalName;
    const address = [data.address, data.city, data.state, data.pincode].filter(Boolean).join(", ");
    setForm((prev) => ({
      ...prev,
      supplierName: data.contactName || prev.supplierName || companyName || "",
      companyName: companyName || prev.companyName,
      panNumber: data.panNumber || prev.panNumber,
      address: address || prev.address,
      contactPerson: data.contactName || prev.contactPerson,
      contactMobile: data.mobile || prev.contactMobile,
    }));
  };

  const runGstLookup = async (gstNumber) => {
    const gst = String(gstNumber || "").trim().toUpperCase();
    if (!isValidGST(gst) || gst === lastGstLookupRef.current || profileLocked) return;
    try {
      lastGstLookupRef.current = gst;
      setGstLookupLoading(true);
      setError("");
      const res = await API.get("/supplier/gst-lookup", { params: { gstNumber: gst } });
      applyGstProfileDetails(res.data || {});
      setMessage("GST details auto-filled.");
    } catch (err) {
      lastGstLookupRef.current = "";
      setError(err?.response?.data?.message || "GST lookup failed.");
    } finally {
      setGstLookupLoading(false);
    }
  };

  const handleGstChange = (value) => {
    const gst = String(value || "").toUpperCase();
    setForm((prev) => ({
      ...prev,
      gstNumber: gst,
      panNumber: gst.length >= 12 ? panFromGST(gst) : prev.panNumber,
    }));
    if (isValidGST(gst)) runGstLookup(gst);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!isValidGST(form.gstNumber)) {
      setError("Please enter a valid GST number");
      return;
    }
    if (!isValidPAN(form.panNumber)) {
      setError("Please enter a valid PAN number");
      return;
    }
    if (String(form.contactMobile || "").trim() && !isValidMobile(form.contactMobile)) {
      setError("Please enter a valid contact mobile (10-15 digits)");
      return;
    }
    if (profileLocked) {
      setError("Profile is locked. Contact your assigned manager for edits.");
      return;
    }
    setSaving(true);
    try {
      if (isUpdate) {
        await API.put("/supplier/profile", form);
        setMessage("Profile updated successfully");
        updateStoredUser({
          fullName: form.companyName || form.supplierName || "Supplier",
          photo: form.logo || "",
          photoUpdatedAt: Date.now(),
        });
      } else {
        try {
          await API.post("/supplier/profile", form);
        } catch (createErr) {
          const msg = String(createErr?.response?.data?.message || "").toLowerCase();
          if (msg.includes("already exists")) {
            await API.put("/supplier/profile", form);
          } else {
            throw createErr;
          }
        }
        setMessage("Profile created successfully");
        updateStoredUser({
          fullName: form.companyName || form.supplierName || "Supplier",
          photo: form.logo || "",
          photoUpdatedAt: Date.now(),
        });
        setIsUpdate(true);
      }
      await load();
    } catch (err) {
      if (err?.response?.data?.profileLocked) {
        setProfileLocked(true);
        setAssignedManager(err?.response?.data?.assignedManager || null);
      }
      setError(err?.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleDocChange = (e) => {
    const { name, files } = e.target;
    if (name === "other") {
      setDocs((prev) => ({ ...prev, other: Array.from(files || []) }));
      return;
    }
    setDocs((prev) => ({ ...prev, [name]: files?.[0] || null }));
  };

  const uploadDocuments = async () => {
    setDocMessage("");
    setDocError("");
    if (profileLocked) {
      setDocError("Profile is locked. Contact your assigned manager for edits.");
      return;
    }
    if (!docs.gstCertificate && !docs.panCard && !docs.bankProof && (!docs.other || docs.other.length === 0)) {
      setDocError("Please select at least one document to upload");
      return;
    }
    setUploadingDocs(true);
    try {
      const fd = new FormData();
      if (docs.gstCertificate) fd.append("gstCertificate", docs.gstCertificate);
      if (docs.panCard) fd.append("panCard", docs.panCard);
      if (docs.bankProof) fd.append("bankProof", docs.bankProof);
      if (docs.other?.length) docs.other.forEach((f) => fd.append("other", f));
      await API.post("/supplier/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocMessage("Documents uploaded successfully");
      setDocs({ gstCertificate: null, panCard: null, bankProof: null, other: [] });
      await load();
    } catch (e) {
      if (e?.response?.data?.profileLocked) {
        setProfileLocked(true);
        setAssignedManager(e?.response?.data?.assignedManager || null);
      }
      setDocError(e?.response?.data?.message || "Failed to upload documents");
    } finally {
      setUploadingDocs(false);
    }
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (profileLocked) {
      setError("Profile is locked. Contact your assigned manager for edits.");
      e.target.value = "";
      return;
    }
    setLogoUploading(true);
    setMessage("");
    setError("");
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await API.put("/supplier/profile", { logo: dataUrl });
      setForm((prev) => ({ ...prev, logo: dataUrl }));
      setLogoPreview(dataUrl);
      updateStoredUser({
        fullName: form.companyName || form.supplierName || "Supplier",
        photo: dataUrl,
        photoUpdatedAt: Date.now(),
      });
      setMessage("Logo updated");
    } catch (err) {
      if (err?.response?.data?.profileLocked) {
        setProfileLocked(true);
        setAssignedManager(err?.response?.data?.assignedManager || null);
      }
      setError(err?.response?.data?.message || "Logo upload failed");
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="danger" />
        <p>Loading profile…</p>
      </div>
    );
  }

  const initials = (form.companyName || form.supplierName || "S")
    .split(" ")
    .map((s) => s.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Container fluid className="mt-4 supplier-profile">
      <Card className="p-3 shadow-sm">
        <Tab.Container defaultActiveKey="basic">
          <Row>
            {/* LEFT: Sidebar */}
            <Col md={3} className="bg-light p-3" style={{ minHeight: "100vh", position: "sticky", top: 0 }}>
              <div className="text-center mb-3">
                <div
                  style={{
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    border: "1px solid #e6e6e6",
                    background: "#fff",
                    margin: "0 auto 10px",
                    display: "grid",
                    placeItems: "center",
                    fontWeight: 800,
                    color: "#b91c1c",
                    overflow: "hidden",
                  }}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Supplier logo"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => { e.currentTarget.src = "/assets/logo.png"; }}
                    />
                  ) : (
                    initials
                  )}
                </div>
                <input
                  ref={fileRefs.logo}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleLogoPick}
                />
                <Button size="sm" variant="outline-primary" onClick={() => fileRefs.logo.current?.click()} disabled={logoUploading || profileLocked}>
                  {logoUploading ? "Uploading..." : "Change Logo"}
                </Button>
                <div className="mt-2">
                  <Badge bg={isUpdate ? "success" : "secondary"}>
                    {isUpdate ? "Existing Profile" : "New Profile"}
                  </Badge>
                </div>
                <div className="mt-3">
                  <div className="small text-muted mb-1">Profile completeness</div>
                  <ProgressBar now={completion.percent} label={`${completion.percent}%`} />
                </div>
              </div>

              <Nav variant="pills" className="flex-column gap-2">
                <Nav.Link eventKey="basic">
                  📌 Basic Information{" "}
                  <Badge bg={completion.sections.basic ? "success" : "secondary"} className="ms-2">
                    {completion.sections.basic ? "Done" : "Req"}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="contact">
                  👤 Contact{" "}
                  <Badge bg={completion.sections.contact ? "success" : "secondary"} className="ms-2">
                    {completion.sections.contact ? "Added" : "Optional"}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="bank">
                  🏦 Bank Details{" "}
                  <Badge bg={completion.sections.bank ? "success" : "secondary"} className="ms-2">
                    {completion.sections.bank ? "Added" : "Optional"}
                  </Badge>
                </Nav.Link>
                <Nav.Link eventKey="documents">
                  📂 Documents{" "}
                  <Badge bg={completion.sections.documents ? "success" : "secondary"} className="ms-2">
                    {completion.sections.documents ? "Added" : "Optional"}
                  </Badge>
                </Nav.Link>
              </Nav>
            </Col>

            {/* RIGHT: Tab Content */}
            <Col md={9} className="p-3">
              <h4 className="mb-2">{isUpdate ? "Update Supplier Profile" : "Complete Supplier Profile"}</h4>
              <div className="text-muted small mb-3">
                Required fields: Company Name, GST, PAN, Address.
              </div>
              {profileLocked && (
                <Alert variant="warning">
                  Profile locked. Contact your assigned manager for edits.
                  {assignedManager?.fullName ? ` ${assignedManager.fullName}` : ""}
                  {assignedManager?.mobile ? ` | ${assignedManager.mobile}` : ""}
                  {assignedManager?.email ? ` | ${assignedManager.email}` : ""}
                </Alert>
              )}
              {message && <Alert variant="success">{message}</Alert>}
              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <fieldset disabled={profileLocked || saving}>
                <Tab.Content>
                  <Tab.Pane eventKey="basic">
                    <h5 className="mb-3">Basic Information</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Supplier Name</Form.Label>
                          <Form.Control name="supplierName" value={form.supplierName} onChange={handleChange} required />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Company Name</Form.Label>
                          <Form.Control name="companyName" value={form.companyName} onChange={handleChange} required />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>GST Number</Form.Label>
                          <Form.Control
                            name="gstNumber"
                            value={form.gstNumber}
                            onChange={(e) => handleGstChange(e.target.value)}
                            isInvalid={!!form.gstNumber && !isValidGST(form.gstNumber)}
                            required
                          />
                          {gstLookupLoading && <Form.Text className="text-primary">Loading GST details...</Form.Text>}
                          <Form.Control.Feedback type="invalid">Enter a valid GST.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>PAN Number</Form.Label>
                          <Form.Control
                            name="panNumber"
                            value={form.panNumber}
                            onChange={(e) => setField("panNumber", e.target.value.toUpperCase())}
                            isInvalid={!!form.panNumber && !isValidPAN(form.panNumber)}
                            required
                          />
                          <Form.Control.Feedback type="invalid">Enter a valid PAN.</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Pickup Address</Form.Label>
                          <Form.Control name="address" value={form.address} onChange={handleChange} required />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Status</Form.Label>
                          <Form.Select name="status" value={form.status} onChange={handleChange}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  <Tab.Pane eventKey="contact">
                    <h5 className="mb-3">Contact</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Contact Person</Form.Label>
                          <Form.Control name="contactPerson" value={form.contactPerson} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Contact Mobile</Form.Label>
                          <Form.Control
                            name="contactMobile"
                            value={form.contactMobile}
                            onChange={handleChange}
                            type="tel"
                            inputMode="numeric"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  <Tab.Pane eventKey="bank">
                    <h5 className="mb-3">Bank Details</h5>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Account Name</Form.Label>
                          <Form.Control name="bank.accountName" value={form.bank.accountName} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Account Number</Form.Label>
                          <Form.Control name="bank.accountNumber" value={form.bank.accountNumber} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>IFSC</Form.Label>
                          <Form.Control name="bank.ifsc" value={form.bank.ifsc} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Bank Name</Form.Label>
                          <Form.Control name="bank.bankName" value={form.bank.bankName} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Branch</Form.Label>
                          <Form.Control name="bank.branch" value={form.bank.branch} onChange={handleChange} />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab.Pane>

                  <Tab.Pane eventKey="documents">
                    <h5 className="mb-3">Documents</h5>
                    {docMessage && <Alert variant="success">{docMessage}</Alert>}
                    {docError && <Alert variant="danger">{docError}</Alert>}
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>GST Certificate</Form.Label>
                          <Form.Control type="file" name="gstCertificate" ref={fileRefs.gstCertificate} onChange={handleDocChange} />
                          {docsExisting.gstCertificate && <small className="text-success">Uploaded</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>PAN Card</Form.Label>
                          <Form.Control type="file" name="panCard" ref={fileRefs.panCard} onChange={handleDocChange} />
                          {docsExisting.panCard && <small className="text-success">Uploaded</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Bank Proof</Form.Label>
                          <Form.Control type="file" name="bankProof" ref={fileRefs.bankProof} onChange={handleDocChange} />
                          {docsExisting.bankProof && <small className="text-success">Uploaded</small>}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Other Documents</Form.Label>
                          <Form.Control type="file" name="other" ref={fileRefs.other} onChange={handleDocChange} multiple />
                          {(docsExisting.other || []).length > 0 && <small className="text-success">{docsExisting.other.length} file(s) uploaded</small>}
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="mt-3">
                      <Button variant="outline-primary" onClick={uploadDocuments} disabled={uploadingDocs}>
                        {uploadingDocs ? "Uploading..." : "Upload Documents"}
                      </Button>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
                </fieldset>

                <div className="d-flex gap-2 mt-4">
                  <Button variant="danger" type="submit" disabled={saving || profileLocked}>
                    {saving ? <Spinner animation="border" size="sm" /> : (isUpdate ? "Update Profile" : "Submit Profile")}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Tab.Container>
      </Card>
    </Container>
  );
};

export default SupplierProfileForm;
